import { X } from 'lucide-react'
import { useCopilotStore, type CopilotInsight } from '../../stores/copilotStore'

interface Props {
  callSid: string
  readOnly?: boolean
}

const CATEGORY_LABELS: Record<string, string> = {
  budget: 'Budget',
  timeline: 'Timeline',
  pain: 'Pain point',
  decision_maker: 'Decision maker',
  objection: 'Objection',
  competitor: 'Competitor',
  contact_preference: 'Contact preference',
  other: 'Other',
}

// Stable empty reference — returning `?? []` from inside a Zustand selector
// produces a fresh array each call and loops useSyncExternalStore.
const EMPTY_INSIGHTS: ReadonlyArray<CopilotInsight> = []

export function InsightNotebook({ callSid, readOnly = false }: Props) {
  const insights = useCopilotStore((s) => s.byCallSid[callSid]?.insights) ?? EMPTY_INSIGHTS
  const dismissInsight = useCopilotStore((s) => s.dismissInsight)
  // Post-call review shows the full record; the live view hides dismissed ones.
  const visible = readOnly ? insights : insights.filter((i) => !i.dismissed)

  return (
    <section className="rounded-lg border border-border bg-surface-card p-4">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Notebook</h3>
        <span className="text-xs text-text-secondary">{visible.length} insight{visible.length === 1 ? '' : 's'}</span>
      </header>
      {visible.length === 0 ? (
        <p className="text-xs text-text-secondary">Nothing recorded yet.</p>
      ) : (
        <ul className="space-y-2">
          {visible.map((insight) => (
            <li
              key={insight.id}
              className="flex items-start justify-between gap-3 rounded border border-border-subtle bg-surface-elevated px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold uppercase text-text-secondary">
                  {CATEGORY_LABELS[insight.category] ?? insight.category}
                </div>
                <div className="text-sm text-text-primary">{insight.summary}</div>
                {insight.evidence && (
                  <div className="mt-1 text-xs italic text-text-secondary">
                    “{insight.evidence}”
                  </div>
                )}
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => dismissInsight(callSid, insight.id)}
                  className="rounded p-1 text-text-secondary hover:bg-surface hover:text-text-primary"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
